import { EventStream, SEED_CLUSTERS, NeuralClassifier, loadModel, loadTrainingSet, dspConfig, TrainingSet, gateAndClassify, PopOnsetGate, } from "@beatbox/voice-core";
import { RhythmRail } from "./rhythm-rail.js";
import { Scorer, timingVerdict, verdictLabel } from "./scoring.js";
import { store } from "../app/state/store.js";
import { pitchCloseness } from "./pitch-target.js";
import { recordRun } from "../storage/progress.js";
import { computeGrade } from "./results.js";
const ONSET_LATENCY_OFFSET_MS = 24;
const PERCUSSIVE_FALLBACK = new Set(["pop"]);
const SUSTAIN_GRACE_MS = 260;
const VERBOSE = false;
function emptyRunStats() {
    return { perfect: 0, early: 0, late: 0, free: 0, miss: 0, sustainsHeld: 0 };
}
export class GameLoop {
    constructor(d) {
        Object.defineProperty(this, "d", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: d
        });
        Object.defineProperty(this, "raf", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "running", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "popGate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new PopOnsetGate()
        });
        Object.defineProperty(this, "rail", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stream", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "scorer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Scorer()
        });
        Object.defineProperty(this, "lastLand", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        Object.defineProperty(this, "classifier", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "onDone", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "timed", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "pitchInvariantLabels", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
        Object.defineProperty(this, "impulseLabels", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
        Object.defineProperty(this, "enabledClusters", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "lastHeartbeat", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "eventsSinceHeartbeat", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "nonNullSinceHeartbeat", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "clusterCountsSinceHeartbeat", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "peakEnergySinceHeartbeat", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "totalEvents", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "landsSinceHeartbeat", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "runStats", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: emptyRunStats()
        });
        Object.defineProperty(this, "leadInEndMs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "lastCountdownShown", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: -1
        });
        Object.defineProperty(this, "courseKey", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: "custom"
        });
        Object.defineProperty(this, "lastResult", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "tick", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (!this.running)
                    return;
                const tickStart = typeof performance !== "undefined" ? performance.now() : 0;
                const nowMs = this.d.ctx.currentTime * 1000;
                const timed = this.timed;
                const events = this.stream.drain();
                let latest = null;
                let popFramesThisBatch = 0;
                let nullFramesThisBatch = 0;
                for (const drained of events) {
                    let ev = drained;
                    latest = ev;
                    this.totalEvents += 1;
                    this.eventsSinceHeartbeat += 1;
                    const evEnergy = ev.features.energy ?? 0;
                    if (evEnergy > this.peakEnergySinceHeartbeat) {
                        this.peakEnergySinceHeartbeat = evEnergy;
                    }
                    // Always re-run our local classifier on the RAW per-frame features
                    // when it is ready, so scoring and the detection trail reflect the
                    // unaggregated per-packet classifier signal rather than any
                    // coarser cluster id/delta the worklet may have attached.
                    if (this.classifier?.ready) {
                        const c = this.classifyFeatures(ev.features);
                        if (c.clusterId) {
                            ev = {
                                ...ev,
                                clusterId: c.clusterId,
                                confidence: c.confidence,
                                delta: {
                                    precision: c.precision,
                                    consistency: ev.delta?.consistency ?? 0,
                                },
                            };
                        }
                        else {
                            // Raw classifier rejected this frame — clear any stale
                            // worklet-assigned cluster so we don't paint aggregated hits.
                            ev = { ...ev, clusterId: null, confidence: 0 };
                        }
                    }
                    if (!ev.clusterId) {
                        nullFramesThisBatch += 1;
                        continue;
                    }
                    if (this.enabledClusters && !this.enabledClusters.has(ev.clusterId)) {
                        nullFramesThisBatch += 1;
                        continue;
                    }
                    this.nonNullSinceHeartbeat += 1;
                    this.clusterCountsSinceHeartbeat[ev.clusterId] =
                        (this.clusterCountsSinceHeartbeat[ev.clusterId] ?? 0) + 1;
                    // Paint a detection trail in this cluster's lane so the player sees
                    // their classified voice propagating down the rail alongside the
                    // required notes.
                    try {
                        this.d.renderer.markDetection?.(ev.clusterId, this.d.colorOf(ev.clusterId), nowMs);
                    }
                    catch (err) {
                        console.error("[GameLoop] markDetection failed", err);
                    }
                    if (this.isPercussive(ev.clusterId)) {
                        popFramesThisBatch += 1;
                        const energy = ev.features.energy ?? 0;
                        const transient = ev.features.transient ?? 0;
                        const now = typeof performance !== "undefined" ? performance.now() : 0;
                        const sinceLast = this.popGate.accept(energy, transient, now);
                        if (sinceLast === null)
                            continue;
                        console.debug(`[GameLoop] POP onset accepted #${this.popGate.count} ` +
                            `(${Number.isFinite(sinceLast) ? sinceLast.toFixed(0) : "∞"}ms ` +
                            `since last) energy=${energy.toFixed(3)} ` +
                            `transient=${transient.toFixed(3)} ` +
                            `conf=${(ev.confidence * 100) | 0}%`);
                    }
                    const landTime = this.isPercussive(ev.clusterId)
                        ? ev.t - ONSET_LATENCY_OFFSET_MS
                        : ev.t;
                    const landed = this.rail.tryLand(ev.clusterId, landTime);
                    if (landed) {
                        this.landsSinceHeartbeat += 1;
                        const verdict = timingVerdict(true, timed, landed.offsetMs, this.d.perfectMs ?? 45);
                        if (verdict === "perfect")
                            this.runStats.perfect += 1;
                        else if (verdict === "early")
                            this.runStats.early += 1;
                        else if (verdict === "late")
                            this.runStats.late += 1;
                        else if (verdict === "free")
                            this.runStats.free += 1;
                        console.debug(`[GameLoop] LAND ${ev.clusterId} → ${verdict.toUpperCase()} ` +
                            `(${landed.offsetMs >= 0 ? "+" : ""}${landed.offsetMs.toFixed(0)}ms) ` +
                            `precision=${(ev.delta.precision * 100) | 0}% ` +
                            `conf=${(ev.confidence * 100) | 0}%`);
                        this.lastLand.set(ev.clusterId, ev.t);
                        const targetHz = landed.target.targetHz;
                        const pitchScore = typeof targetHz === "number"
                            ? pitchCloseness(ev.features.pitchHz ?? 0, targetHz)
                            : undefined;
                        const hud = this.scorer.advance(store.hud.get(), {
                            landed: true,
                            delta: ev.delta,
                            timingOffsetMs: landed.offsetMs,
                            timed,
                            pitchScore,
                        });
                        store.hud.set(hud);
                        try {
                            this.d.renderer.spark(this.d.laneOf(ev.clusterId), ev.delta.precision, this.d.colorOf(ev.clusterId), verdictLabel(verdict), "hit");
                            this.d.renderer.setComboTier?.(this.comboTierFor(hud.combo));
                            this.d.sonifier.hit(ev.delta.precision, ev.features.centroid);
                            this.d.haptics.hit(ev.delta.precision);
                        }
                        catch (err) {
                            console.error("[GameLoop] feedback error on hit", err);
                        }
                        if (hud.combo > 0 && hud.combo % 5 === 0) {
                            console.info(`[GameLoop] 🔥 combo milestone ${hud.combo}x`);
                            this.d.sonifier.combo(hud.combo);
                            this.d.haptics.combo();
                        }
                        this.feedSustain(ev.clusterId, nowMs);
                    }
                    else {
                        this.feedSustain(ev.clusterId, nowMs);
                    }
                }
                if (latest) {
                    this.d.renderer.setDetection({
                        clusterId: latest.clusterId,
                        confidence: latest.confidence,
                        precision: latest.delta.precision,
                    });
                }
                else {
                    this.d.renderer.setDetection({
                        clusterId: null,
                        confidence: 0,
                        precision: 0,
                    });
                }
                const misses = this.rail.collectMisses(nowMs);
                if (misses.length > 0) {
                    console.debug(`[GameLoop] ${misses.length} target(s) drifted past the line — ` +
                        `recovery engaged (${misses.map((m) => m.clusterId).join(",")})`);
                }
                {
                    const remaining = this.rail.targets.filter((t) => !t.hit).length;
                    if (remaining < 8)
                        this.rail.extend();
                }
                for (const _m of misses) {
                    this.runStats.miss += 1;
                    const hud = this.scorer.advance(store.hud.get(), {
                        landed: false,
                        delta: { precision: 0, consistency: 0 },
                        timingOffsetMs: 999,
                        timed,
                    });
                    store.hud.set(hud);
                    this.d.renderer.setComboTier?.(0);
                    try {
                        this.d.renderer.shake?.(1);
                    }
                    catch (err) {
                        console.error("[GameLoop] shake error on miss", err);
                    }
                    try {
                        this.d.renderer.spark(this.d.laneOf(_m.clusterId), 0, this.d.colorOf(_m.clusterId), "MISS", "miss");
                    }
                    catch (err) {
                        console.error("[GameLoop] feedback error on miss", err);
                    }
                }
                try {
                    this.d.renderer.feed(latest);
                    if (nowMs < this.leadInEndMs) {
                        const remainMs = this.leadInEndMs - nowMs;
                        const beatMs = this.rail.beatMs;
                        const remainBeats = Math.ceil(remainMs / beatMs);
                        if (remainBeats !== this.lastCountdownShown) {
                            this.lastCountdownShown = remainBeats;
                            this.d.renderer.announceCountdown?.(remainBeats);
                        }
                    }
                    else if (this.lastCountdownShown !== 0) {
                        this.lastCountdownShown = 0;
                        this.d.renderer.announceCountdown?.(0);
                    }
                    const lookaheadMs = this.rail.beatMs * 4;
                    this.d.renderer.render(this.rail.targets, this.d.laneOf, this.d.colorOf, nowMs, timed, lookaheadMs);
                }
                catch (err) {
                    console.error("[GameLoop] render error; stopping loop", err);
                    this.running = false;
                    this.onDone?.();
                    return;
                }
                if (this.rail.complete()) {
                    const hud = store.hud.get();
                    console.info(`[GameLoop] ✅ course complete — score=${hud.score} ` +
                        `bestCombo=${this.scorer.getBestCombo()}x ` +
                        `agility=${(hud.agility * 100) | 0} ` +
                        `recovery=${(hud.recovery * 100) | 0}%`);
                    this.running = false;
                    void this.finishRun(hud.score);
                    return;
                }
                {
                    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
                    if (this.lastHeartbeat === 0)
                        this.lastHeartbeat = now;
                    if (now - this.lastHeartbeat >= 1000) {
                        const dtSec = (now - this.lastHeartbeat) / 1000;
                        const counts = Object.entries(this.clusterCountsSinceHeartbeat)
                            .map(([k, v]) => `${k}=${v}`)
                            .join(",") || "none";
                        const detId = latest?.clusterId ?? "null";
                        const detConf = latest ? (latest.confidence * 100) | 0 : 0;
                        const engineReady = this.d.engine.isReady?.() ?? "unknown";
                        if (this.eventsSinceHeartbeat === 0) {
                            console.warn(`[GameLoop] ⚠ input heartbeat: NO ENGINE EVENTS in ` +
                                `${dtSec.toFixed(1)}s — mic/engine may be silent or ` +
                                `stalled (engineReady=${engineReady}). Check mic ` +
                                `permission, audio graph, and worklet.`);
                        }
                        else {
                            if (VERBOSE)
                                console.info(`[GameLoop] input heartbeat: events/s=` +
                                    `${(this.eventsSinceHeartbeat / dtSec).toFixed(0)} ` +
                                    `classified=${this.nonNullSinceHeartbeat} ` +
                                    `null=${this.eventsSinceHeartbeat - this.nonNullSinceHeartbeat} ` +
                                    `peakEnergy=${this.peakEnergySinceHeartbeat.toFixed(3)} ` +
                                    `categories[${counts}] lands=${this.landsSinceHeartbeat} ` +
                                    `latest=${detId}@${detConf}% totalEvents=${this.totalEvents}`);
                            if (this.nonNullSinceHeartbeat === 0) {
                                console.warn(`[GameLoop] ⚠ receiving audio frames but NONE are ` +
                                    `being classified (all null). Either energy is below ` +
                                    `the gate (peakEnergy=${this.peakEnergySinceHeartbeat.toFixed(3)}) ` +
                                    `or the classifier is rejecting everything. Check ` +
                                    `Loudness gain / Silence gate and that a model is ` +
                                    `trained for the enabled sounds.`);
                            }
                        }
                        this.lastHeartbeat = now;
                        this.eventsSinceHeartbeat = 0;
                        this.nonNullSinceHeartbeat = 0;
                        this.clusterCountsSinceHeartbeat = {};
                        this.peakEnergySinceHeartbeat = 0;
                        this.landsSinceHeartbeat = 0;
                    }
                }
                if (tickStart) {
                    const dur = performance.now() - tickStart;
                    if (dur > 12) {
                        console.warn(`[GameLoop] slow tick ${dur.toFixed(1)}ms ` +
                            `(events=${events.length})`);
                    }
                }
                this.raf = requestAnimationFrame(this.tick);
            }
        });
        this.stream = new EventStream(d.engine);
    }
    async start(course, timed, onDone) {
        console.info(`[GameLoop] start course=${course.id} timed=${timed} ` +
            `sequence=${course.sequence.length} tempo=${course.tempoBpm}`);
        this.popGate.reset();
        this.lastHeartbeat = 0;
        this.eventsSinceHeartbeat = 0;
        this.nonNullSinceHeartbeat = 0;
        this.clusterCountsSinceHeartbeat = {};
        this.peakEnergySinceHeartbeat = 0;
        this.totalEvents = 0;
        this.landsSinceHeartbeat = 0;
        if (!this.d.engine.isReady?.()) {
            console.warn("[GameLoop] engine not reported ready at start; events may be delayed");
        }
        this.scorer.reset();
        this.runStats = emptyRunStats();
        this.lastCountdownShown = -1;
        this.courseKey =
            course.id === "designed" ? `designed:${course.title}` : course.id;
        store.hud.set({
            agility: 0,
            combo: 0,
            recovery: 1,
            precision: 0,
            score: 0,
            consistency: 0,
            lastVerdict: "none",
            lastGain: 0,
            lastOffsetMs: 0,
            lastEventAt: 0,
        });
        if (this.d.basePoints != null || this.d.comboCap != null) {
            this.scorer.configure(this.d.basePoints ?? 100, this.d.comboCap ?? 20, this.d.perfectMs);
        }
        else if (this.d.perfectMs != null) {
            this.scorer.configure(100, 20, this.d.perfectMs);
        }
        this.d.engine.reset();
        this.d.engine.setClusters(SEED_CLUSTERS);
        await this.applyProfile();
        this.d.engine.setCalibrating(false);
        this.d.engine.setConfig({
            frameHopMs: dspConfig.frameHopMs,
            energyGate: dspConfig.energyGate,
            energyScale: dspConfig.energyScale,
            noiseBandLoHz: dspConfig.noiseBandLoHz,
            noiseBandHiHz: dspConfig.noiseBandHiHz,
            voicingBandHiHz: dspConfig.voicingBandHiHz,
            transientScale: dspConfig.transientScale,
        });
        this.lastLand.clear();
        this.onDone = onDone;
        this.timed = timed;
        this.d.renderer.setFrameHopMs?.(dspConfig.frameHopMs);
        const startMs = this.d.ctx.currentTime * 1000;
        const beatMs = 60000 / course.tempoBpm;
        const LEAD_IN_BEATS = 2;
        this.leadInEndMs = startMs + LEAD_IN_BEATS * beatMs;
        const sustainChance = this.d.sustainChance ?? 0.4;
        const sustainBuckets = [
            { beats: 1, weight: this.d.sustainWeight1 ?? 1 },
            { beats: 2, weight: this.d.sustainWeight2 ?? 1 },
            { beats: 4, weight: this.d.sustainWeight4 ?? 1 },
            { beats: 8, weight: this.d.sustainWeight8 ?? 0.5 },
        ];
        const pickSustainBeats = () => {
            const total = sustainBuckets.reduce((a, b) => a + Math.max(0, b.weight), 0);
            if (total <= 0)
                return 1;
            let r = Math.random() * total;
            for (const b of sustainBuckets) {
                r -= Math.max(0, b.weight);
                if (r <= 0)
                    return b.beats;
            }
            return sustainBuckets[sustainBuckets.length - 1].beats;
        };
        this.rail = new RhythmRail({
            sequence: course.sequence,
            tempoBpm: course.tempoBpm,
            timed,
            windowMs: course.windowMs,
            pitches: course.pitches,
            infinite: course.infinite,
            spawnIntervalBeats: this.d.spawnIntervalBeats ?? 1,
            frameHopMs: dspConfig.frameHopMs,
            breakBeats: this.d.breakBeats ?? 1,
            plannedNotes: course.plannedNotes,
            rollSustainMs: (id, bMs) => {
                if (course.plannedNotes && course.plannedNotes.length > 0) {
                    return 0;
                }
                if (this.isPercussive(id))
                    return 0;
                if (Math.random() >= sustainChance) {
                    return 0;
                }
                const beats = pickSustainBeats();
                return Math.round(beats * bMs);
            },
            sustainMsFor: (id) => this.isPercussive(id) ? 0 : Math.min(beatMs * 0.9, 600),
        }, startMs);
        this.running = true;
        this.tick();
    }
    stop() {
        if (this.running)
            console.info("[GameLoop] stop");
        this.running = false;
        cancelAnimationFrame(this.raf);
    }
    getLastResult() {
        return this.lastResult;
    }
    async finishRun(score) {
        const bestCombo = this.scorer.getBestCombo();
        const stats = { ...this.runStats };
        const landed = stats.perfect + stats.early + stats.late + stats.free;
        const attempts = landed + stats.miss;
        const accuracy = attempts > 0 ? landed / attempts : 0;
        const grade = computeGrade(accuracy, stats.perfect, attempts);
        let isNewBest = false;
        let best = { bestScore: score, bestCombo, runs: 1 };
        try {
            const rec = await recordRun(this.courseKey, score, bestCombo);
            isNewBest = rec.isNewBest;
            best = rec.best;
        }
        catch (err) {
            console.warn("[GameLoop] failed to record run progress", err);
        }
        this.lastResult = {
            score,
            bestCombo,
            grade,
            accuracy,
            stats,
            isNewBest,
            best: {
                bestScore: best.bestScore,
                bestCombo: best.bestCombo,
                runs: best.runs,
            },
            courseKey: this.courseKey,
        };
        console.info(`[GameLoop] run finished grade=${grade} accuracy=` +
            `${(accuracy * 100) | 0}% newBest=${isNewBest}`);
        this.onDone?.();
    }
    dispose() {
        console.info("[GameLoop] dispose");
        this.stop();
        this.stream.dispose();
        try {
            this.d.renderer.dispose?.();
        }
        catch (err) {
            console.warn("[GameLoop] renderer dispose failed", err);
        }
    }
    setPitchInvariantLabels(labels) {
        this.pitchInvariantLabels = new Set(labels);
    }
    setImpulseLabels(labels) {
        this.impulseLabels = new Set(labels);
    }
    setEnabledClusters(ids) {
        const set = new Set(ids);
        this.enabledClusters = set.size > 0 ? set : null;
        console.info("[GameLoop] enabled clusters restricted to: " +
            `[${this.enabledClusters ? [...this.enabledClusters].join(",") : "all"}]`);
    }
    isPercussive(clusterId) {
        if (this.d.percussiveOf)
            return this.d.percussiveOf(clusterId);
        return PERCUSSIVE_FALLBACK.has(clusterId);
    }
    comboTierFor(combo) {
        if (combo >= 30)
            return 3;
        if (combo >= 15)
            return 2;
        if (combo >= 5)
            return 1;
        return 0;
    }
    classifyFeatures(f) {
        if (!this.classifier || !this.classifier.ready) {
            return { clusterId: null, confidence: 0, precision: 0 };
        }
        const { clusterId, confidence, delta } = gateAndClassify(f, this.classifier);
        if (clusterId &&
            this.enabledClusters &&
            !this.enabledClusters.has(clusterId)) {
            return { clusterId: null, confidence: 0, precision: 0 };
        }
        return { clusterId, confidence, precision: delta.precision };
    }
    feedSustain(clusterId, nowMs) {
        if (this.isPercussive(clusterId))
            return;
        const done = this.rail.updateSustain(clusterId, nowMs, SUSTAIN_GRACE_MS);
        if (done) {
            this.runStats.sustainsHeld += 1;
            console.debug(`[GameLoop] SUSTAIN complete ${clusterId} ` +
                `(held ${(done.heldMs ?? 0).toFixed(0)}ms / ` +
                `req ${done.sustainMs.toFixed(0)}ms)`);
            const hud = this.scorer.advance(store.hud.get(), {
                landed: true,
                delta: { precision: 1, consistency: 1 },
                timed: false,
            });
            store.hud.set(hud);
            try {
                this.d.renderer.spark(this.d.laneOf(clusterId), 1, this.d.colorOf(clusterId), "HELD!", "hit");
                this.d.sonifier.combo(hud.combo);
                this.d.haptics.combo();
            }
            catch (err) {
                console.error("[GameLoop] feedback error on sustain", err);
            }
        }
    }
    async applyProfile() {
        try {
            const saved = await loadModel();
            if (saved && saved.labels.length >= 2) {
                const classifier = new NeuralClassifier();
                await classifier.load(saved);
                if (classifier.ready) {
                    this.d.engine.setClassifier(classifier);
                    this.classifier = classifier;
                    console.info("[GameLoop] using persisted trained model " +
                        `(labels: ${saved.labels.join(",")})`);
                    return;
                }
            }
            const corpus = await loadTrainingSet();
            if (corpus) {
                const ts = TrainingSet.fromJSON(corpus.data);
                if (ts.labels().length >= 2) {
                    const classifier = new NeuralClassifier();
                    classifier.setPitchInvariantLabels(this.pitchInvariantLabels);
                    if (this.impulseLabels.size > 0) {
                        classifier.configure({
                            impulseLabels: [...this.impulseLabels],
                        });
                    }
                    await classifier.train(ts.toFrames());
                    if (classifier.ready) {
                        this.d.engine.setClassifier(classifier);
                        this.classifier = classifier;
                        console.info("[GameLoop] retrained classifier from persisted " +
                            `corpus (labels: ${ts.labels().join(",")})`);
                        return;
                    }
                }
            }
            {
                this.d.engine.setClassifier(null);
                this.classifier = null;
                console.info("[GameLoop] no trained model; using seed clusters fallback");
            }
        }
        catch (err) {
            console.warn("[GameLoop] failed to load trained model", err);
        }
    }
}
